/**
 * Map-anchored quick-edit popup for the currently SELECTED waypoint.
 *
 * Triggered by the route layer's `setSelectedWaypointId(id)` (which fires
 * on a click on a waypoint circle). Renders a Mapbox Marker hosting a
 * React portal at the waypoint's lng/lat with:
 *   - vertical altitude slider, clamped to dock.defaults.maxAltM (so the
 *     user can't exceed the dock's flight envelope without an explicit
 *     edit elsewhere)
 *   - heading input (deg 0–360 with Shift+arrow snapping handled by the
 *     numeric input's step attribute)
 *
 * Closed on:
 *   - Click outside the popup
 *   - Escape
 *   - Selection change (id flips to a different waypoint → popup re-anchors)
 *
 * Lives at EditorPage level (NOT inside the route layer) so the layer
 * file stays side-effect-only and this popup can use a regular React
 * lifecycle.
 */

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import mapboxgl from 'mapbox-gl';
import { useMapStore } from '../../stores';
import { useDockDemoStore } from '../../stores/dockDemoStore';
import './WaypointMapEditor.css';

export default function WaypointMapEditor() {
  const map = useMapStore((s) => s.map);
  const selectedWaypointId = useDockDemoStore((s) => s.selectedWaypointId);
  const setSelectedWaypointId = useDockDemoStore((s) => s.setSelectedWaypointId);
  const missions = useDockDemoStore((s) => s.missions);
  const selectedMissionId = useDockDemoStore((s) => s.selectedMissionId);
  const dockStations = useDockDemoStore((s) => s.dockStations);
  const updateWaypoint = useDockDemoStore((s) => s.updateWaypoint);

  const hostRef = useRef<HTMLDivElement | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);

  const mission = missions.find((m) => m.id === selectedMissionId) ?? null;
  const wp = mission?.waypoints.find((w) => w.id === selectedWaypointId) ?? null;
  const dock = mission?.dockStationId
    ? dockStations.find((d) => d.id === mission.dockStationId) ?? null
    : null;
  const maxAlt = dock?.defaults.maxAltM ?? 120;

  // Mount / unmount the Mapbox marker as selection toggles. The host div is
  // owned by the ref so React can portal-render into it without remounting
  // the marker on every state change.
  useEffect(() => {
    if (!map || !wp) {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      return;
    }
    if (!hostRef.current) {
      hostRef.current = document.createElement('div');
      hostRef.current.className = 'dock-demo-wp-editor-host';
    }
    if (!markerRef.current) {
      markerRef.current = new mapboxgl.Marker({
        element: hostRef.current,
        anchor: 'left',
        offset: [18, 0],
      });
    }
    markerRef.current.setLngLat([wp.lng, wp.lat]).addTo(map);
    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
    };
    // wp.lng / wp.lat in deps so dragging the waypoint moves the popup too.
  }, [map, wp]);

  // Close on click outside / Esc. The CANVAS exception lets the user click
  // around the map without dismissing the popup unless they actually click
  // off the host. Selection change handled by the wp-changes effect above
  // (the host re-anchors to the new waypoint automatically).
  useEffect(() => {
    if (!wp) return;
    const onClickAway = (ev: MouseEvent) => {
      const host = hostRef.current;
      if (!host) return;
      if (host.contains(ev.target as Node)) return;
      if ((ev.target as HTMLElement | null)?.tagName === 'CANVAS') return;
      setSelectedWaypointId(null);
    };
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        setSelectedWaypointId(null);
      }
    };
    window.addEventListener('mousedown', onClickAway);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('mousedown', onClickAway);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [wp, setSelectedWaypointId]);

  if (!mission || !wp || !hostRef.current) return null;

  const idx = mission.waypoints.findIndex((w) => w.id === wp.id);

  return createPortal(
    <div className="dock-demo-wp-editor" role="dialog">
      <div className="dock-demo-wp-editor__head">
        <span className="dock-demo-wp-editor__idx">#{idx + 1}</span>
        <button
          type="button"
          className="dock-demo-wp-editor__close"
          onClick={() => setSelectedWaypointId(null)}
          title="Close"
        >
          ×
        </button>
      </div>
      <label className="dock-demo-wp-editor__field">
        <span>
          Alt <strong>{wp.alt} m</strong>
        </span>
        <input
          type="range"
          min={0}
          max={maxAlt}
          step={1}
          value={wp.alt}
          onChange={(e) =>
            updateWaypoint(mission.id, wp.id, { alt: Number(e.target.value) || 0 })
          }
          title={`Max ${maxAlt} m (dock's defaults.maxAltM)`}
        />
      </label>
      <label className="dock-demo-wp-editor__field">
        <span>Heading</span>
        <input
          type="number"
          min={0}
          max={360}
          step={5}
          placeholder="auto"
          value={wp.heading ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            updateWaypoint(mission.id, wp.id, {
              heading: v === '' ? undefined : Number(v),
            });
          }}
          title="0=N, 90=E, 180=S, 270=W. Empty = auto-follow next waypoint. Step 5°."
        />
      </label>
    </div>,
    hostRef.current,
  );
}
